import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Map, AdvancedMarker, Pin, useMap } from '@vis.gl/react-google-maps';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import './MapComponent.css';

const MapComponent = ({
  center,
  events,
  loading,
  activeEventId,
  hoveredEventId,
  setActiveEventId,
  onMapMove,
}) => {
  const map = useMap();
  const [markers, setMarkers] = useState({});
  const [geocodedPositions, setGeocodedPositions] = useState({});
  const [isGeocoding, setIsGeocoding] = useState(false);
  const clusterer = useRef(null);
  const isMobileOrTablet = window.innerWidth <= 768;


  // Geocode all venue names on events update
  useEffect(() => {
    let isActive = true;

    const geocodeVenues = async () => {
      if (!events?.length) {
        if (isActive) {
          setGeocodedPositions({});
          setIsGeocoding(false);
        }
        return;
      }

      setIsGeocoding(true);
      const positions = {};
      for (const event of events) {
        const venueName = event._embedded?.venues?.[0]?.name;
        if (!venueName) continue;

        try {
          const res = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
              venueName
            )}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
          );
          const data = await res.json();
          const loc = data.results?.[0]?.geometry?.location;
          if (loc) {
            positions[event.id] = { lat: loc.lat, lng: loc.lng };
          }
        } catch (error) {
          console.warn(`❌ Geocoding failed for ${venueName}:`, error);
        }
      }
      if (isActive) {
        setGeocodedPositions(positions);
        setIsGeocoding(false);
      }
    };

    geocodeVenues();

    return () => {
      isActive = false;
    };
  }, [events]);

  // Clear and reset markers when events change
  useEffect(() => {
    setMarkers({});
    clusterer.current?.clearMarkers();
  }, [events]);

  const setMarkerRef = useCallback((marker, key) => {
    setMarkers((prev) => {
      if (prev[key] === marker) return prev;
      const updated = { ...prev };
      if (marker) updated[key] = marker;
      else delete updated[key];
      return updated;
    });
  }, []);

  useEffect(() => {
    if (!map) return;
    clusterer.current = new MarkerClusterer({ map });
    return () => clusterer.current?.clearMarkers();
  }, [map]);

  useEffect(() => {
    if (!map) return;

    let firstIdle = true;

    const handleIdle = () => {
      const bounds = map.getBounds();
      const center = map.getCenter();
      if (!bounds || !center) return;

      if (firstIdle) {
        firstIdle = false;
        return;
      }

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const lat = center.lat();
      const lng = center.lng();
      const latMiles = Math.abs(ne.lat() - sw.lat()) * 69;
      const lngMiles = Math.abs(ne.lng() - sw.lng()) * 54.6;
      const radius = Math.min(100, Math.max(1, Math.round(Math.sqrt(latMiles ** 2 + lngMiles ** 2) / 2)));

      onMapMove({ lat, lng, radius });
    };

    const idleListener = map.addListener('idle', handleIdle);
    const clickListener = map.addListener('click', () => setActiveEventId(null));

    return () => {
      idleListener.remove();
      clickListener.remove();
    };
  }, [map, onMapMove, setActiveEventId]);

  useEffect(() => {
    if (!clusterer.current) return;
    clusterer.current.clearMarkers();
    clusterer.current.addMarkers(Object.values(markers));
  }, [markers]);

  const handleMarkerClick = (eventId) => {
    setActiveEventId(eventId);
  };

  return (
    <div className="map-container">
      {loading ? (
        <p className="map-loading">Loading map...</p>
      ) : (
        <Map
          mapId="507f7730c5c2775c"
          defaultZoom={12}
          maxZoom={17}
          defaultCenter={center}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={false}
          zoomControl={!isMobileOrTablet}
          cameraControl={false}
          gestureHandling="greedy" // Enables single finger drag
          onLoad={() => console.log('✅ Map loaded')}
          onError={(error) => console.error('❌ Map error:', error)}
          style={{ width: '100%', height: '100%' }}
        >
          {events.map((event) => {
            const position = geocodedPositions[event.id];
            if (!position) return null;

            const isHighlighted =
              event.id === activeEventId || event.id === hoveredEventId;

            return (
              <AdvancedMarker
                key={event.id}
                position={position}
                ref={(marker) => marker && setMarkerRef(marker, event.id)}
                onClick={() => handleMarkerClick(event.id)}
              >
                <Pin
                  background={isHighlighted ? '#4285F4' : '#EA4335'}
                  glyphColor={isHighlighted ? '#2C63BF' : '#960A0A'}
                  borderColor={isHighlighted ? '#2C63BF' : '#960A0A'}
                />
              </AdvancedMarker>
            );
          })}
        </Map>
      )}
      {!loading && isGeocoding && (
        <div className="map-markers-loading" aria-live="polite">
          <div className="map-markers-dot" aria-hidden="true" />
          <span>Loading event locations…</span>
        </div>
      )}
    </div>
  );
};

export default MapComponent;
